/* =============================================================
   StudentSidebar.jsx — Student portal navigation sidebar
   -------------------------------------------------------------
   Fixed left sidebar for all student screens. Mirrors the admin
   Sidebar in behaviour (expand/collapse) but uses student-specific
   routes and labels.

   Sections:
   - Logo + "Student Portal" label + collapse toggle
   - Main navigation links for the student portal
   - Settings, user info, and sign-out at the bottom
   ============================================================= */

import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { useAuth }    from '../context/AuthContext';


/* -------------------------------------------------------------
   NAVIGATION ITEMS
   ------------------------------------------------------------- */
const mainNav = [
  { label: 'Dashboard',              icon: 'dashboard',    path: '/student'                },
  { label: 'My Exchange University', icon: 'school',       path: '/student/university'     },
  { label: 'My Documents',           icon: 'folder_open',  path: '/student/documents'      },
  { label: 'Student Experiences',    icon: 'auto_stories', path: '/student/experiences'    },
  { label: 'Discounts',              icon: 'local_offer',  path: '/student/discounts'      },
  { label: 'AI Assistant',           icon: 'smart_toy',    path: '/student/ai-assistant'   },
];


/* -------------------------------------------------------------
   STUDENT SIDEBAR COMPONENT
   ------------------------------------------------------------- */
export default function StudentSidebar({ topOffset = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params]  = useSearchParams();
  const { open, toggle } = useSidebar();
  const { user, logout } = useAuth();

  // In admin preview mode the ?as=<studentId> param must be preserved
  // across all sidebar navigation so the preview stays active.
  const asParam    = params.get('as');
  const withPreview = (path) => asParam ? `${path}?as=${asParam}` : path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Exact match for /student dashboard; prefix match for all other routes
  const isActive = (path) => {
    if (path === '/student') return location.pathname === '/student';
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className="fixed left-0 h-full bg-[#0F172A] flex flex-col z-40 transition-all duration-300"
      style={{ width: open ? '280px' : '72px', top: topOffset }}
    >
      {/* Logo */}
      <div className="px-3 py-4 flex items-center border-b border-slate-800" style={{ minHeight: '64px' }}>
        <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-white shadow-sm">
          <img src="/favicon.png" alt="Unixplore" className="w-full h-full object-contain" />
        </div>
        {open && (
          <div className="ml-3 flex-1 min-w-0 overflow-hidden">
            <p className="text-white font-bold font-display text-base leading-tight whitespace-nowrap">Unixplore</p>
            <p className="text-indigo-400 text-xs leading-tight mt-0.5 whitespace-nowrap font-medium">Student Portal</p>
          </div>
        )}
      </div>

      {/* Floating toggle — white circle on the right edge, always visible */}
      <button
        onClick={toggle}
        title={open ? 'Collapse sidebar' : 'Expand sidebar'}
        className="absolute -right-3 top-[22px] w-6 h-6 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-indigo-600 hover:shadow-lg transition-all z-50"
      >
        <span className="material-symbols-outlined text-[14px]">
          {open ? 'chevron_left' : 'chevron_right'}
        </span>
      </button>

      {/* Main navigation links */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto scrollbar-hide overflow-x-hidden">
        {open && (
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider px-2 mb-2 whitespace-nowrap">
            My Exchange
          </p>
        )}
        <ul className="space-y-1">
          {mainNav.map((item) => (
            <li key={item.path}>
              <button
                onClick={() => navigate(withPreview(item.path))}
                title={!open ? item.label : undefined}
                className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 text-left ${
                  open ? 'px-3 py-2.5' : 'justify-center px-0 py-2.5'
                } ${
                  isActive(item.path)
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                }`}
              >
                <span className="material-symbols-outlined text-[20px] leading-none flex-shrink-0">{item.icon}</span>
                {open && <span className="truncate">{item.label}</span>}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom section: settings, user info, sign out */}
      <div className="px-2 pb-3 border-t border-slate-800 overflow-x-hidden">
        <button
          onClick={() => navigate(withPreview('/student/settings'))}
          title={!open ? 'Settings' : undefined}
          className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 text-left mt-3 ${
            open ? 'px-3 py-2.5' : 'justify-center px-0 py-2.5'
          } ${
            isActive('/student/settings')
              ? 'bg-indigo-600 text-white'
              : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
          }`}
        >
          <span className="material-symbols-outlined text-[20px] leading-none flex-shrink-0">settings</span>
          {open && <span className="truncate">Settings</span>}
        </button>

        {/* Logged-in student info */}
        <div className={`flex items-center mt-3 gap-3 ${open ? 'px-3 py-2' : 'justify-center py-2'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${user?.avatarBg ?? 'bg-sky-500'}`}>
            {user?.initials ?? 'TB'}
          </div>
          {open && (
            <div className="flex-1 min-w-0">
              <p className="text-slate-200 text-sm font-medium truncate">{user?.name ?? 'Tom Baker'}</p>
              <p className="text-slate-500 text-xs truncate">{user?.title ?? 'MIM · Exchange 2026/27'}</p>
            </div>
          )}
        </div>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          title={!open ? 'Sign out' : undefined}
          className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors mt-0.5 text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 ${open ? 'px-3 py-2' : 'justify-center px-0 py-2.5'}`}
        >
          <span className="material-symbols-outlined text-[20px] leading-none flex-shrink-0">logout</span>
          {open && <span className="truncate">Sign out</span>}
        </button>
      </div>
    </div>
  );
}
